'use client';

import { useRef, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { AlertTriangle } from 'lucide-react';
import {
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_VALUE_INK,
  ATTRIBUTE_VALUE_TEXT,
  AttributeSegmentedBoolean,
  attributeLabelWidthStyle,
  attributePlaceholder
} from '@/components/attributes';
import type { Contact } from '@/lib/services/contacts/types';
import { getContactDisplayName } from '@/lib/services/contacts/utils';
import type {
  PropertyTransferSubFlow,
  BeneficiaryHoldingType
} from '@/lib/services/redemption';
import {
  SUB_FLOW_LABELS,
  SUB_FLOW_ICONS,
  instructionCardClass,
  instructionLabelClass,
  instructionPanelClass,
  type TransferBeneficiary
} from './constants';
import { InstructionChoiceMark } from '../instruction-choice-mark';
import { sameKeySet, useOptimisticValue } from '../use-optimistic-value';
import { INSTRUCTION_CHOICE_SIZE } from '../instruction-chrome';
import {
  ATTRIBUTE_BARE_CONTROL,
  AttributeEditableCell,
  AttributeRow
} from '../attribute-row';

// =============================================================================
// Transfer sub-flow
// =============================================================================

export function TransferSubFlowSection({
  transferSubFlow,
  isDirectAvailable,
  propertyState,
  onTransferSubFlowChange,
  compact = false
}: {
  transferSubFlow?: PropertyTransferSubFlow | null;
  isDirectAvailable: boolean;
  propertyState?: string | null;
  onTransferSubFlowChange?: (subFlow: PropertyTransferSubFlow) => void;
  /** The attribute treatment's ink and type — see `instructionLabelClass`. */
  compact?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className={instructionLabelClass(compact)}>Transfer Method</label>
      <div className="grid grid-cols-2 gap-2">
        {(['direct-to-beneficiary', 'via-executor'] as const).map((sf) => {
          const isActive = (transferSubFlow ?? 'direct-to-beneficiary') === sf;
          const SfIcon = SUB_FLOW_ICONS[sf];
          return (
            <button
              key={sf}
              type="button"
              onClick={() => onTransferSubFlowChange?.(sf)}
              className={cn(
                'border text-left transition-all',
                compact
                  ? INSTRUCTION_CHOICE_SIZE
                  : 'rounded-lg px-3 py-2 text-sm',
                instructionCardClass(compact, isActive)
              )}
            >
              <div className="flex items-center justify-between gap-1.5">
                <span className="flex min-w-0 items-center gap-1.5">
                  <SfIcon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{SUB_FLOW_LABELS[sf]}</span>
                </span>
                <InstructionChoiceMark compact={compact} isActive={isActive} />
              </div>
            </button>
          );
        })}
      </div>
      {!isDirectAvailable &&
        (transferSubFlow ?? 'direct-to-beneficiary') ===
          'direct-to-beneficiary' &&
        !!propertyState && (
          /*
            Compact drops the filled band for an inline caution line: full-bleed
            amber was "full-bleed and shouts" against 13px slate rows, and the
            sentence is a caveat on the choice above it rather than an alert about
            the record.
          */
          <div
            className={cn(
              'flex items-center gap-1.5',
              compact
                ? 'px-1.5 pt-0.5'
                : 'bg-warning-subtle rounded-md px-3 py-1.5'
            )}
          >
            <AlertTriangle className="text-warning h-3.5 w-3.5 shrink-0" />
            <p
              className={cn(
                'text-warning-subtle-foreground',
                compact ? 'text-12px' : 'text-xs'
              )}
            >
              Direct transfer may not be available in {propertyState}
            </p>
          </div>
        )}
    </div>
  );
}

// =============================================================================
// Beneficiary selection
// =============================================================================

/**
 * The name snapshot a newly-added beneficiary carries (VALE-355). Extracted from an
 * inline IIFE: React Compiler cannot optimise an immediately-invoked function inside
 * JSX, and the lint rule that flags it is worth listening to on a list that re-renders
 * on every keystroke of a share input.
 */
function contactSnapshot(contacts: Contact[], key: string) {
  const contact = contacts.find((candidate) => String(candidate.id) === key);
  return contact
    ? {
        id: contact.id,
        firstName: contact.firstName ?? '',
        lastName: contact.lastName ?? ''
      }
    : undefined;
}

export function BeneficiarySelection({
  contacts,
  transferBeneficiaries,
  beneficiaryHoldingType,
  onBeneficiaryToggle,
  onBeneficiariesChange,
  onBeneficiaryHoldingTypeChange,
  onBeneficiaryPercentageChange,
  compact = false
}: {
  compact?: boolean;
  contacts: Contact[];
  transferBeneficiaries?: TransferBeneficiary[];
  beneficiaryHoldingType?: BeneficiaryHoldingType | null;
  onBeneficiaryToggle: (contact: Contact) => void;
  /**
   * Set the whole transfer-beneficiary list at once (VALE-355). Supplied only by
   * the compact branch's host — see the `onValueChange` note on why a per-contact
   * toggle loses writes when two land inside one round-trip.
   */
  onBeneficiariesChange?: (
    next: TransferBeneficiary[]
  ) => void | Promise<unknown>;
  onBeneficiaryHoldingTypeChange?: (
    type: BeneficiaryHoldingType | null
  ) => void;
  onBeneficiaryPercentageChange?: (
    contactId: number,
    percentage: number | undefined
  ) => void;
}) {
  const selected = transferBeneficiaries ?? [];

  /*
    **What the chips show, which is not always what the record says yet.** The record
    only updates when the `PUT` returns, so a controlled `ToggleGroup` fed from it did
    not visibly respond to a click — and a second click inside that window diffed
    against a selection the reader could no longer see, and cancelled itself out.
    {@link useOptimisticValue} carries the whole argument.
  */
  const committedKeys = selected.map((b) => String(b.contactId));
  const chosen = useOptimisticValue(committedKeys, sameKeySet);
  const shownKeys = chosen.shown;

  /*
    **`Tab` moves between share rows** (VALE-355). These are `AttributeEditableCell`s,
    which each own their open state — there is no `AttributeGrid` here to own a `Tab`
    chain, so the chain hands focus to the neighbouring row's read-out button and
    activates it, which is what a click does. Keyed off rendered order, so `Shift+Tab`
    is just `-1`.

    Querying the DOM rather than holding a ref per row: the list's length changes as
    beneficiaries are toggled, and a ref array would have to be reconciled with that
    on every render for no gain.
  */
  const shareListRef = useRef<HTMLDivElement | null>(null);
  const stepShareRow = (index: number, delta: number) => {
    const buttons = shareListRef.current?.querySelectorAll<HTMLButtonElement>(
      '[data-share-row] button'
    );
    buttons?.[index + delta]?.click();
  };

  const shownSelected: TransferBeneficiary[] = shownKeys.map(
    (key) =>
      selected.find((b) => String(b.contactId) === key) ?? {
        contactId: Number(key)
      }
  );
  const showPercentages =
    beneficiaryHoldingType === 'tenants-in-common' && shownSelected.length > 1;
  const totalPercentage = showPercentages
    ? parseFloat(
        shownSelected
          .reduce((sum, b) => sum + (b.percentage ?? 0), 0)
          .toFixed(2)
      )
    : 0;
  /*
    **Bound to `getContactDisplayName`, not re-derived** (VALE-355).

    This was `[firstName, lastName].filter(Boolean).join(' ')`, which returns `''`
    for an **organisation** contact — they carry `organisationName` and no personal
    name — so a charity beneficiary rendered as a **blank chip** with nothing in it.
    Reported as *"sometimes there's kind of empty beneficiaries"*; on matter 1 it is
    the Australian Red Cross Society.

    The shared helper already handles the organisation branch, the middle name and
    the `(deceased)` suffix, and `ShareInstructionSection` an inch away was already
    using it — so this was a second, drifting definition of one fact, and the copy
    that drifted was the broken one.
  */
  const contactName = (contact: Contact) => getContactDisplayName(contact);

  /*
    **In compact the ticks become a multi-select `ToggleGroup`** (round 6).

    Review asked for "a better component if we have one", and we do: the same
    `ToggleGroup` the `Yes` / `No` boolean is built on, in its `multiple` mode. Three
    things it fixes at once —

    · **It is a standard component doing what it is for.** A checkbox column is a
      hand-assembled multi-select; `type="multiple"` *is* a multi-select, with the
      roving focus and the `aria-pressed` semantics already correct.
    · **It reads as the same kind of choice as its neighbours.** Instruction and
      transfer method are already tinted chips through `instructionCardClass`; the
      beneficiaries were the one selection in the block wearing a different idiom.
    · **It removes the last checkbox from the surface** — and with it the 13.6px
      glyph problem that no swap of primitive could solve.

    The percentages move out of the rows and under them, because a number input
    cannot live inside a toggle button (every keystroke would land on the toggle).
    That is a gain rather than a compromise: they only exist for tenants-in-common,
    they must sum to the whole, and reading them as a short list is how you check
    that — which is the same reason the total sits with them.
  */
  if (compact) {
    return (
      <div className="space-y-1.5">
        <label className={instructionLabelClass(true)}>Transfer to</label>
        <ToggleGroup
          type="multiple"
          /*
            **The shown selection is optimistic** (VALE-355 round 5).

            `ToggleGroup` is controlled, and the value it was given came from the
            *record* — which only updates after `onBeneficiaryToggle` has round-tripped
            a `PUT`. So a click did not visibly land until the server answered, and a
            second click inside that window diffed against a stale `selected` and
            cancelled itself out. Reported as *"sometimes we click, it doesn't
            select"*.

            `shownKeys` is what the reader last asked for; it is dropped the moment the
            record agrees with it (the ref compare below), so the record stays the only
            durable truth and this is purely a latency mask.
          */
          value={shownKeys}
          onValueChange={(next) => {
            /*
              `ToggleGroup` reports the whole next selection; the parent's handler
              takes one contact and flips it. Diffing against what is *shown* rather
              than against the record is the other half of the fix above — diffing
              against a value the reader can no longer see is what dropped clicks.
            */
            /*
              **The whole next list, built from what is shown** (VALE-355 round 6).

              Per-contact `onBeneficiaryToggle` calls were a lost update: the parent
              rebuilds the array from the *record*, so two clicks inside one
              round-trip both computed from the same stale list and the second write
              overwrote the first — select A then B quickly and A vanished. Sending
              the full intended selection makes each write self-contained.

              `onBeneficiariesChange` is optional so the form variant's checkbox path
              keeps the per-contact handler it has always used; only this branch, with
              its optimistic view, has a next-list to send.
            */
            if (onBeneficiariesChange) {
              const byId = new Map(
                selected.map((b) => [String(b.contactId), b])
              );
              chosen.apply(next, () =>
                onBeneficiariesChange(
                  next.map(
                    (key) =>
                      byId.get(key) ?? {
                        contactId: Number(key),
                        contact: contactSnapshot(contacts, key)
                      }
                  )
                )
              );
              return;
            }
            const before = new Set(shownKeys);
            const after = new Set(next);
            contacts.forEach((contact) => {
              const key = String(contact.id);
              if (before.has(key) !== after.has(key))
                onBeneficiaryToggle(contact);
            });
          }}
          className="flex-wrap justify-start gap-1.5"
          aria-label="Transfer to"
        >
          {contacts.map((contact) => (
            <ToggleGroupItem
              key={contact.id}
              value={String(contact.id)}
              className={cn(
                'gap-1.5 border',
                INSTRUCTION_CHOICE_SIZE,
                instructionCardClass(
                  true,
                  shownKeys.includes(String(contact.id))
                )
              )}
            >
              {contactName(contact)}
              <InstructionChoiceMark
                compact
                reserveSpace
                isActive={shownKeys.includes(String(contact.id))}
              />
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        {shownSelected.length > 1 && (
          <HoldingTypePanel
            compact
            beneficiaryHoldingType={beneficiaryHoldingType}
            onBeneficiaryHoldingTypeChange={onBeneficiaryHoldingTypeChange}
            showPercentages={showPercentages}
            totalPercentage={totalPercentage}
          >
            {showPercentages && (
              /*
                **The one-column label width, because this panel is one column**
                (VALE-355). These rows inherit `--attr-label-w` from the block above,
                which publishes the *two*-column formula (~124px) — so
                `Jane Smith (deceased)` was clipped while the value beside it held a
                three-character `50%` and most of the row sat empty. The panel is
                full width and stacks one row per beneficiary, so the one-column
                formula is the honest one and it is ~30px wider.
              */
              <div
                ref={shareListRef}
                className="flex flex-col"
                style={attributeLabelWidthStyle(1)}
              >
                {shownSelected.map((beneficiary, index) => {
                  const contact = contacts.find(
                    (c) => c.id === beneficiary.contactId
                  );
                  if (!contact) return null;
                  return (
                    <BeneficiaryShareRow
                      key={beneficiary.contactId}
                      name={contactName(contact)}
                      percentage={beneficiary.percentage}
                      onCommit={(next) =>
                        onBeneficiaryPercentageChange?.(
                          beneficiary.contactId,
                          next
                        )
                      }
                      onStep={(delta) => stepShareRow(index, delta)}
                    />
                  );
                })}
              </div>
            )}
          </HoldingTypePanel>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <label className={instructionLabelClass(compact)}>Transfer to</label>
      <div className="space-y-1">
        {contacts.map((contact) => {
          const isSelected = (transferBeneficiaries ?? []).some(
            (b) => b.contactId === contact.id
          );
          // Same helper as the compact branch — an organisation beneficiary rendered
          // a blank row here too (VALE-355).
          const name = getContactDisplayName(contact);
          return (
            <div
              key={contact.id}
              className={cn(
                'flex items-center gap-2 rounded-md transition-colors',
                compact
                  ? /*
                      **No border in compact, and no dead full-width box.** An
                      isolated review measured "~760px of container for ~90px of
                      text" and read the bordered row as a foreign, heavier language
                      than the rows around it. A tick plus a name on a hover target
                      is the same affordance without the box — and `w-fit` stops the
                      row claiming the width it has nothing to put in.
                    */
                    cn(
                      'hover:bg-surface-subtle w-fit px-1.5 py-0.5',
                      isSelected && 'bg-surface-subtle'
                    )
                  : cn(
                      'border px-3 py-1.5',
                      isSelected
                        ? 'border-border-strong bg-surface-subtle/40'
                        : 'border-border-subtle bg-card'
                    )
              )}
            >
              {/*
                **This one stays a checkbox, and that is not an oversight.** The
                segmented `Yes` / `No` that replaced the value-confirmation tick is
                a *binary* control; picking which of N beneficiaries receive the
                property is a multi-select, and a column of Yes/No pairs would be
                both larger and harder to scan than a column of ticks. What compact
                changed was the size — and that override is gone, because the shared
                checkbox now defaults to `md` (17px, rounded, with an inset tick).
                Passing it here would restate the default.
              */}
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onBeneficiaryToggle(contact)}
                id={`prop-ben-inv-${contact.id}`}
              />
              <Label
                htmlFor={`prop-ben-inv-${contact.id}`}
                className={cn(
                  'flex-1 cursor-pointer',
                  compact
                    ? `${ATTRIBUTE_VALUE_TEXT} ${ATTRIBUTE_VALUE_INK}`
                    : 'text-foreground text-sm'
                )}
              >
                {name}
              </Label>
              {showPercentages && isSelected && (
                <div className="relative w-24 shrink-0">
                  <Input
                    type="number"
                    min={0.01}
                    max={99.99}
                    step="0.01"
                    className="h-7 pr-7 text-right text-sm"
                    value={
                      selected.find((s) => s.contactId === contact.id)
                        ?.percentage ?? ''
                    }
                    onChange={(e) => {
                      const val = e.target.value
                        ? parseFloat(e.target.value)
                        : undefined;
                      onBeneficiaryPercentageChange?.(contact.id, val);
                    }}
                  />
                  <span className="text-foreground-subtle absolute top-1/2 right-2 -translate-y-1/2 text-xs">
                    %
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(transferBeneficiaries ?? []).length > 1 && (
        <HoldingTypePanel
          compact={compact}
          beneficiaryHoldingType={beneficiaryHoldingType}
          onBeneficiaryHoldingTypeChange={onBeneficiaryHoldingTypeChange}
          showPercentages={showPercentages}
          totalPercentage={totalPercentage}
        />
      )}
    </div>
  );
}

/**
 * **One beneficiary's share, as an attribute row** (VALE-355 round 5).
 *
 * ## Why it is a row and not a bare input
 *
 * It was a permanently-open `Input` in a hand-laid-out flex line, and review asked
 * for *"the same components the normal text fields use ... the same shape as the
 * Title Reference"*. This is that shape: a label in the shared column, a read-out
 * at rest (`50%`), an editor in the value's own slot on click — the same
 * `AttributeRow` + {@link AttributeEditableCell} pair the `Ownership type` and
 * `Ownership share` rows above it already use.
 *
 * ## Why that also fixes the lag
 *
 * The old input was **controlled by the record** and committed on every keystroke:
 * each character fired `onTransferBeneficiariesChange`, which is a real `PUT`, and
 * the value the reader saw came back from the server. So typing `50` felt slow and
 * out of sync — reported as *"they are slow when you change, it's not instant"*.
 * That is the per-keystroke-write shape `AttributeGrid`'s own header calls out: the
 * draft is local and the save fires on commit.
 *
 * So the draft lives here, in local state, and the commit happens once — on blur,
 * on `Enter`, or when the cell closes. Typing is instant because nothing but React
 * is involved; the total below recomputes from the committed record, which is what
 * makes it trustworthy rather than merely fast.
 *
 * `Escape` reverts to the value the row opened with, matching every other editor on
 * the surface.
 */
function BeneficiaryShareRow({
  name,
  percentage,
  onCommit,
  onStep
}: {
  name: string;
  percentage?: number;
  onCommit: (next: number | undefined) => void;
  /** Commit and hand the editor to the row `delta` places away. */
  onStep: (delta: number) => void;
}) {
  return (
    // The marker the list's `Tab` chain finds this row's read-out button by.
    <div data-share-row>
      <AttributeRow label={name}>
        <AttributeEditableCell
          display={percentage != null ? `${percentage}%` : ''}
          placeholder={attributePlaceholder('Share')}
          renderEditor={(close) => (
            <ShareEditor
              initial={percentage}
              onDone={(next) => {
                onCommit(next);
                close();
              }}
              onCancel={close}
              onStep={(next, delta) => {
                onCommit(next);
                close();
                /*
                  After the close, so the row this hands focus to is not immediately
                  re-closed by the blur of the editor being torn down.
                */
                requestAnimationFrame(() => onStep(delta));
              }}
            />
          )}
        />
      </AttributeRow>
    </div>
  );
}

/**
 * The share editor: a plain text field with a `%` in its own padding.
 *
 * `inputMode="decimal"` rather than `type="number"` — the stepper arrows ignore this
 * surface's type and palette, appear only on hover, and are laid out *inside* the
 * content box where they clip a legitimate `99.99`. Same trade `AttributeMoneyEditor`
 * makes for `$`.
 *
 * The `%` is **chrome, not content**: it sits in the padding, so the caret cannot
 * land before it and a paste cannot end up on the wrong side of it — the `$`
 * prefix's argument, mirrored.
 */
function ShareEditor({
  initial,
  onDone,
  onCancel,
  onStep
}: {
  initial?: number;
  onDone: (next: number | undefined) => void;
  onCancel: () => void;
  onStep: (next: number | undefined, delta: number) => void;
}) {
  const [text, setText] = useState(initial != null ? String(initial) : '');
  /* The value the row opened with, so `Escape` has something to revert to. */
  const openedWithRef = useRef(initial);

  /** The committed value for the current text, or the opened-with value if unusable. */
  const resolve = () => {
    const raw = text.trim();
    if (!raw) return undefined;
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : openedWithRef.current;
  };
  const commit = () => onDone(resolve());

  return (
    <div className="flex min-w-0 flex-1 items-center">
      <Input
        autoFocus
        type="text"
        inputMode="decimal"
        aria-label="Share"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          } else if (event.key === 'Tab') {
            /*
              Commit and open the next share row, the same thing `Tab` does on a grid
              row — a column of shares that all need filling in is exactly the case
              that behaviour exists for. `preventDefault` because the browser's own
              next stop is the `%` glyph's cell, not the next row.
            */
            event.preventDefault();
            onStep(resolve(), event.shiftKey ? -1 : 1);
          }
        }}
        className={cn(ATTRIBUTE_BARE_CONTROL, 'pr-0')}
      />
      {/*
        **The `%` sits in the cell beside the field, not pinned to its right edge.**

        It was `text-right` with an absolutely-positioned `%`, and typing then
        started at the *right* of the row — which is both surprising and against this
        treatment's own rule that values are left-aligned (`AttributeGrid`'s header:
        right-aligning gave "a card of ragged left edges"). Left-aligned, a `%`
        pinned to the far right would float detached from a two-digit number.

        In flow immediately after the input it stays glued to the number, which is
        exactly the move `Ownership share` above already makes — and the same one the
        money row makes with its `$`. Chrome in the cell, content in the field.
      */}
      <span
        aria-hidden
        className={cn(
          ATTRIBUTE_VALUE_TEXT,
          ATTRIBUTE_MUTED_INK,
          'shrink-0 pr-1.5'
        )}
      >
        %
      </span>
    </div>
  );
}

/**
 * The "how will they hold it?" sub-question — one component, because both chromes
 * render it identically apart from ink and type, and the compact branch above would
 * otherwise be a second copy of it.
 */
function HoldingTypePanel({
  compact,
  beneficiaryHoldingType,
  onBeneficiaryHoldingTypeChange,
  showPercentages,
  totalPercentage,
  children
}: {
  compact: boolean;
  beneficiaryHoldingType?: BeneficiaryHoldingType | null;
  onBeneficiaryHoldingTypeChange?: (
    type: BeneficiaryHoldingType | null
  ) => void;
  showPercentages: boolean;
  totalPercentage: number;
  /**
   * The per-beneficiary share inputs, when this holding type asks for them
   * (VALE-355). Rendered **inside** the panel, between the choice and the total,
   * because the three are one sub-question: *how will they hold it → in what
   * shares → do those add up*. They used to sit above the panel entirely, so
   * answering `Tenants in Common` made share fields appear behind the reader's
   * pointer, above the button just pressed; putting them below the panel fixed
   * that but left `Total` above the numbers it sums. Inside, both read downward.
   */
  children?: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-1.5 p-2.5', instructionPanelClass(compact))}>
      <Label
        className={cn(
          compact
            ? `${ATTRIBUTE_LABEL_TEXT} text-muted-foreground font-medium`
            : 'text-foreground-secondary text-xs font-medium'
        )}
      >
        How will the beneficiaries hold the property?
      </Label>
      <div className="grid grid-cols-2 gap-1.5">
        {(['joint-tenants', 'tenants-in-common'] as const).map((ht) => (
          <button
            key={ht}
            type="button"
            onClick={() => onBeneficiaryHoldingTypeChange?.(ht)}
            className={cn(
              'flex items-center justify-between gap-1.5 border text-left transition-all',
              compact
                ? INSTRUCTION_CHOICE_SIZE
                : 'rounded-md px-2.5 py-1.5 text-sm',
              instructionCardClass(compact, beneficiaryHoldingType === ht)
            )}
          >
            <span className="truncate">
              {ht === 'joint-tenants' ? 'Joint Tenants' : 'Tenants in Common'}
            </span>
            <InstructionChoiceMark
              compact={compact}
              isActive={beneficiaryHoldingType === ht}
            />
          </button>
        ))}
      </div>
      {children}
      {showPercentages && (
        <p
          className={cn(
            compact ? 'text-12px' : 'text-xs',
            totalPercentage === 100
              ? 'text-success-subtle-foreground'
              : 'text-warning-subtle-foreground'
          )}
        >
          Total: {totalPercentage}%
          {totalPercentage !== 100 && ' (must equal 100%)'}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Contrary intention
// =============================================================================

/**
 * **A binary that becomes `Yes` / `No` in compact** — the second checkbox the
 * LF-6314 review objected to, and the one where the objection is strongest.
 *
 * "Contrary intention — estate pays off mortgage" is a *finding about the will*.
 * An unticked box cannot distinguish "the will says nothing of the kind" from "no
 * one has read the will yet", and on this particular fact that ambiguity decides
 * who pays out a mortgage. `Yes` / `No` states it. (Renamed from
 * `ContraryIntentionCheckbox` because after this it is only a checkbox in one of
 * its two chromes — a name that says `Checkbox` while rendering a segmented control
 * is exactly the kind of lying name the root `CLAUDE.md` bans.)
 */
export function ContraryIntentionControl({
  hasContraryIntention,
  onContraryIntentionChange,
  compact = false
}: {
  hasContraryIntention?: boolean;
  onContraryIntentionChange?: (value: boolean) => void;
  compact?: boolean;
}) {
  const title = 'Contrary intention: estate pays off mortgage';
  const explanation =
    'The will directs the estate to pay off the mortgage before transferring.';

  if (compact) {
    return (
      <div className={cn('space-y-1.5 p-2.5', instructionPanelClass(true))}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p
              className={cn(
                ATTRIBUTE_VALUE_TEXT,
                ATTRIBUTE_VALUE_INK,
                'font-medium'
              )}
            >
              {title}
            </p>
            <p
              className={cn(
                ATTRIBUTE_LABEL_TEXT,
                'text-muted-foreground mt-0.5'
              )}
            >
              {explanation}
            </p>
          </div>
          <AttributeSegmentedBoolean
            value={!!hasContraryIntention}
            onValueChange={(next) => onContraryIntentionChange?.(next)}
            ariaLabel={title}
            className="shrink-0"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-start gap-2 p-2.5',
        instructionPanelClass(false)
      )}
    >
      <Checkbox
        id="contrary-intention-inv"
        checked={!!hasContraryIntention}
        onCheckedChange={(val) => onContraryIntentionChange?.(val === true)}
      />
      <div>
        <Label
          htmlFor="contrary-intention-inv"
          className="text-foreground cursor-pointer text-sm font-medium"
        >
          {title}
        </Label>
        <p className="text-muted-foreground mt-0.5 text-xs">{explanation}</p>
      </div>
    </div>
  );
}
